import { useCallback } from 'react';
import { create } from 'zustand';
import { sidekickModelIDs } from './redesign/components/sidekickModels';

export type TableSortEntry = { id: string; desc: boolean };

export type Preferences = {
  isUseCodeEditor: boolean;
  openKbarUsingCmdShiftK: boolean;
  /** Drives the voice orbs' render loop (VoiceSphere + SidekickOrb). */
  animateOrb: boolean;
  /**
   * The schema playground (/b/{id}/schema-bot) shows its call settings panel
   * (pipeline, models, output, knowledge base, caller memory); hidden by
   * default — one summary line instead. Toggled from the page or Cmd+K.
   */
  showSchemaBotSettings: boolean;
  /** The Sidekick's collapsed orb launcher in the corner; toggled from Cmd+K. */
  showSidekickOrb: boolean;
  selectedBotId?: string;
  /** Which LLM the Sidekick runs on, per user — see sidekickModels.ts. */
  sidekickModel?: string;
  tableColumnOrders: Record<string, string[]>;
  tableColumnSizings: Record<string, Record<string, number>>;
  tableSortings: Record<string, TableSortEntry[]>;
  tableColumnVisibility: Record<string, Record<string, boolean>>;
};

export const DEFAULT_PREFERENCES: Preferences = {
  isUseCodeEditor: true,
  openKbarUsingCmdShiftK: false,
  animateOrb: true,
  showSchemaBotSettings: false,
  showSidekickOrb: true,
  tableColumnOrders: {},
  tableColumnSizings: {},
  tableSortings: {},
  tableColumnVisibility: {},
};

const PREFERENCES_STORAGE_KEY = 'voxcore.preferences.v1';

type PreferenceKey = keyof Preferences;
type AuthHeadersProvider = () => Record<string, string>;

let authHeadersProvider: AuthHeadersProvider = () => ({});

export function configurePreferencesAuthHeaders(provider: AuthHeadersProvider) {
  authHeadersProvider = provider;
}

function normalizeTableColumnOrders(input: unknown): Record<string, string[]> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

  const normalized: Record<string, string[]> = {};
  Object.entries(input as Record<string, unknown>).slice(0, 100).forEach(([rawTableId, rawOrder]) => {
    const tableId = rawTableId.trim();
    if (!tableId || tableId.length > 128 || !Array.isArray(rawOrder)) return;

    const seen = new Set<string>();
    const order: string[] = [];
    rawOrder.slice(0, 250).forEach((value) => {
      if (typeof value !== 'string') return;
      const columnId = value.trim();
      if (!columnId || columnId.length > 128 || seen.has(columnId)) return;
      seen.add(columnId);
      order.push(columnId);
    });
    normalized[tableId] = order;
  });
  return normalized;
}

// Mirrors the backend's clamp range (user_preferences.go).
const MIN_TABLE_COLUMN_WIDTH = 16;
const MAX_TABLE_COLUMN_WIDTH = 4000;

function normalizeTableColumnSizings(input: unknown): Record<string, Record<string, number>> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

  const normalized: Record<string, Record<string, number>> = {};
  Object.entries(input as Record<string, unknown>).slice(0, 100).forEach(([rawTableId, rawSizing]) => {
    const tableId = rawTableId.trim();
    if (!tableId || tableId.length > 128) return;
    if (!rawSizing || typeof rawSizing !== 'object' || Array.isArray(rawSizing)) return;

    const sizing: Record<string, number> = {};
    Object.entries(rawSizing as Record<string, unknown>).slice(0, 250).forEach(([rawColumnId, rawWidth]) => {
      const columnId = rawColumnId.trim();
      if (!columnId || columnId.length > 128) return;
      if (typeof rawWidth !== 'number' || !Number.isFinite(rawWidth)) return;
      sizing[columnId] = Math.round(
        Math.min(MAX_TABLE_COLUMN_WIDTH, Math.max(MIN_TABLE_COLUMN_WIDTH, rawWidth)),
      );
    });
    normalized[tableId] = sizing;
  });
  return normalized;
}

function normalizeTableColumnVisibility(input: unknown): Record<string, Record<string, boolean>> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

  const normalized: Record<string, Record<string, boolean>> = {};
  Object.entries(input as Record<string, unknown>).slice(0, 100).forEach(([rawTableId, rawVisibility]) => {
    const tableId = rawTableId.trim();
    if (!tableId || tableId.length > 128) return;
    if (!rawVisibility || typeof rawVisibility !== 'object' || Array.isArray(rawVisibility)) return;

    const visibility: Record<string, boolean> = {};
    Object.entries(rawVisibility as Record<string, unknown>).slice(0, 250).forEach(([rawColumnId, visible]) => {
      const columnId = rawColumnId.trim();
      if (!columnId || columnId.length > 128) return;
      if (typeof visible !== 'boolean') return;
      visibility[columnId] = visible;
    });
    normalized[tableId] = visibility;
  });
  return normalized;
}

function normalizeTableSortings(input: unknown): Record<string, TableSortEntry[]> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

  const normalized: Record<string, TableSortEntry[]> = {};
  Object.entries(input as Record<string, unknown>).slice(0, 100).forEach(([rawTableId, rawEntries]) => {
    const tableId = rawTableId.trim();
    if (!tableId || tableId.length > 128 || !Array.isArray(rawEntries)) return;

    const seen = new Set<string>();
    const entries: TableSortEntry[] = [];
    rawEntries.slice(0, 10).forEach((rawEntry) => {
      if (!rawEntry || typeof rawEntry !== 'object') return;
      const { id, desc } = rawEntry as { id?: unknown; desc?: unknown };
      if (typeof id !== 'string') return;
      const columnId = id.trim();
      if (!columnId || columnId.length > 128 || seen.has(columnId)) return;
      seen.add(columnId);
      entries.push({ id: columnId, desc: Boolean(desc) });
    });
    normalized[tableId] = entries;
  });
  return normalized;
}

function normalizePreferences(input: unknown): Preferences {
  const obj = input && typeof input === 'object' ? input as Partial<Preferences> : {};
  return {
    ...DEFAULT_PREFERENCES,
    ...(typeof obj.isUseCodeEditor === 'boolean' ? { isUseCodeEditor: obj.isUseCodeEditor } : {}),
    ...(typeof obj.openKbarUsingCmdShiftK === 'boolean'
      ? { openKbarUsingCmdShiftK: obj.openKbarUsingCmdShiftK }
      : {}),
    ...(typeof obj.animateOrb === 'boolean' ? { animateOrb: obj.animateOrb } : {}),
    ...(typeof obj.showSchemaBotSettings === 'boolean'
      ? { showSchemaBotSettings: obj.showSchemaBotSettings }
      : {}),
    ...(typeof obj.showSidekickOrb === 'boolean' ? { showSidekickOrb: obj.showSidekickOrb } : {}),
    // Validated against the registry, not just typed: a retired id would be
    // sent on every helper connect and silently ignored by the backend, so the
    // panel would show one model while the session ran another.
    ...(typeof obj.sidekickModel === 'string' && sidekickModelIDs().includes(obj.sidekickModel)
      ? { sidekickModel: obj.sidekickModel }
      : {}),
    ...(typeof obj.selectedBotId === 'string' && obj.selectedBotId.trim()
      ? { selectedBotId: obj.selectedBotId.trim() }
      : {}),
    tableColumnOrders: normalizeTableColumnOrders(obj.tableColumnOrders),
    tableColumnSizings: normalizeTableColumnSizings(obj.tableColumnSizings),
    tableSortings: normalizeTableSortings(obj.tableSortings),
    tableColumnVisibility: normalizeTableColumnVisibility(obj.tableColumnVisibility),
  };
}

function storageKeyForUser(userKey: string | null): string {
  return userKey ? `${PREFERENCES_STORAGE_KEY}.${userKey}` : PREFERENCES_STORAGE_KEY;
}

/**
 * View-only preferences that keep working when the backend cannot store them
 * (an older server that rejects the key — e.g. a local page pointed at a
 * deployed backend through the backend switcher): a failed save keeps the new
 * value in this browser instead of rolling it back, and a server reply
 * without the key keeps the browser's value.
 */
const LOCAL_FALLBACK_KEYS: PreferenceKey[] = ['showSchemaBotSettings', 'showSidekickOrb'];

/** `fromServer` with the browser's value for every fallback key the server did not send. */
function keepLocalFallbacks(fromServer: Preferences, data: unknown, local: Preferences): Preferences {
  const sent = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const out = { ...fromServer };
  for (const key of LOCAL_FALLBACK_KEYS) {
    if (!(key in sent)) (out as Record<string, unknown>)[key] = local[key];
  }
  return out;
}

function readStoredPreferences(userKey: string | null = null): Preferences {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(storageKeyForUser(userKey))
      || window.localStorage.getItem(PREFERENCES_STORAGE_KEY);
    const preferences = raw ? normalizePreferences(JSON.parse(raw)) : DEFAULT_PREFERENCES;
    writeStoredPreferences(preferences, userKey);
    return preferences;
  } catch {
    writeStoredPreferences(DEFAULT_PREFERENCES, userKey);
    return DEFAULT_PREFERENCES;
  }
}

function writeStoredPreferences(preferences: Preferences, userKey: string | null = null) {
  if (typeof window === 'undefined') return;
  try {
    const encoded = JSON.stringify(preferences);
    window.localStorage.setItem(PREFERENCES_STORAGE_KEY, encoded);
    if (userKey) window.localStorage.setItem(storageKeyForUser(userKey), encoded);
  } catch {
    // Ignore unavailable storage.
  }
}

async function readPreferenceError(res: Response): Promise<string> {
  const text = await res.text();
  return text || `HTTP ${res.status}`;
}

type PreferencesState = {
  preferences: Preferences;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  userKey: string | null;
  loadPreferences: (userKey: string, headers: Record<string, string>) => Promise<void>;
  useLocalPreferences: (userKey?: string | null) => void;
  setPreference: <K extends PreferenceKey>(key: K, value: Preferences[K]) => Promise<void>;
};

export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  preferences: readStoredPreferences(),
  loading: false,
  loaded: true,
  error: null,
  userKey: null,

  loadPreferences: async (userKey, headers) => {
    const state = get();
    if (state.loaded && state.userKey === userKey) return;
    if (state.loading && state.userKey === userKey) return;

    set({
      preferences: readStoredPreferences(userKey),
      loading: true,
      loaded: true,
      error: null,
      userKey,
    });
    try {
      const res = await fetch('/api/user/preferences', {
        headers,
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(await readPreferenceError(res));
      const data = await res.json();
      const preferences = keepLocalFallbacks(normalizePreferences(data), data, get().preferences);
      writeStoredPreferences(preferences, userKey);
      set({
        preferences,
        loading: false,
        loaded: true,
        error: null,
        userKey,
      });
    } catch (err) {
      set({
        loading: false,
        loaded: true,
        error: err instanceof Error ? err.message : String(err),
        userKey,
      });
    }
  },

  useLocalPreferences: (userKey = null) => {
    set({
      preferences: readStoredPreferences(userKey),
      loading: false,
      loaded: true,
      error: null,
      userKey,
    });
  },

  setPreference: async (key, value) => {
    const prev = get().preferences;
    const next = { ...prev, [key]: value };
    const userKey = get().userKey;
    writeStoredPreferences(next, userKey);
    set({ preferences: next, error: null });

    const headers = authHeadersProvider();
    if (!headers.Authorization) {
      // Logged-out / unconfigured surfaces can use defaults in memory, but
      // there is no per-user identity to persist against.
      return;
    }

    try {
      const res = await fetch('/api/user/preferences', {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: value }),
      });
      if (!res.ok) throw new Error(await readPreferenceError(res));
      const data = await res.json();
      const preferences = keepLocalFallbacks(normalizePreferences(data), data, next);
      writeStoredPreferences(preferences, userKey);
      set({ preferences, loaded: true, error: null });
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      if (LOCAL_FALLBACK_KEYS.includes(key)) {
        // Kept in this browser (already written above): the toggle still works.
        set({ error });
        return;
      }
      writeStoredPreferences(prev, userKey);
      set({
        preferences: prev,
        error,
      });
      throw err;
    }
  },
}));

export function usePreferences() {
  return usePreferencesStore((state) => ({
    preferences: state.preferences,
    loading: state.loading,
    loaded: state.loaded,
    error: state.error,
  }));
}

export function usePreference<K extends PreferenceKey>(
  key: K,
): [Preferences[K], (value: Preferences[K]) => Promise<void>] {
  const value = usePreferencesStore((state) => state.preferences[key]);
  const update = usePreferencesStore((state) => state.setPreference);
  const setValue = useCallback((next: Preferences[K]) => update(key, next), [key, update]);
  return [value, setValue];
}

export function getPreferences(): Preferences {
  return usePreferencesStore.getState().preferences;
}

export async function setPreference<K extends PreferenceKey>(
  key: K,
  value: Preferences[K],
): Promise<void> {
  await usePreferencesStore.getState().setPreference(key, value);
}
